import React from "react"
import { useTranslation } from "react-i18next"

import * as BikeApi from "features/bike/api"
import { ErrorResponse } from "models"
import { useAlert, useForm, useOperator } from "hooks"
import {
	BikeRegistrationActionType,
	useBikeRegistrationProcess,
	useBikesStore,
} from "features/dashboard/hooks"
import { IS_FNUCI_SYNC, sanitizeCode } from "utils"

import { FormElement, formStatusEnum } from "components/Form"
import { Button, Spinner } from "components/Elements"
import { BICYCODE_ID } from "operators/bicycode/config"
import { DECATHLON_ID } from "operators/decathlon/config"
import { MFC_ID } from "operators/mfc/config"

type InitialFormProps = {
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const defaultFormFields = {
	bike_id: "",
}
const BIKE_ID_LENGTH = 7

export const InitialForm: React.FC<InitialFormProps> = ({ setModalIsOpen }) => {
	const { bikeRegistrationDispatch } = useBikeRegistrationProcess()
	const { bikesStore } = useBikesStore()
	const {
		// prettier-ignore
		bikes: bikesList,
	} = bikesStore

	const {
		formFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: defaultFormFields,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { currentOperator } = useOperator()

	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	const handleCodePassword = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		const bikeId = sanitizeCode(formFields.bike_id)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const { res: bikePasswordInfo, getError } = await BikeApi.getBikePassword(bikeId)
				if (getError()) throw getError()

				const isBikeOwnerMailKnown =
					bikePasswordInfo?.censor_mail && bikePasswordInfo?.send_code_by_mail

				if (isBikeOwnerMailKnown) {
					// We have info in API response (censor_mail and send_code_by_mail), we redirect user to the 2nd step : code validation
					bikeRegistrationDispatch({
						type: BikeRegistrationActionType.CODE_VALIDATION_STEP,
						bikeInfo: bikePasswordInfo,
						bikeId: bikeId,
						bike: null,
					})
				} else {
					// We redirect user to register bike page
					bikeRegistrationDispatch({
						type: BikeRegistrationActionType.BIKE_REGISTRATION_STEP,
						bikeId: bikeId,
						bikeInfo: null,
						bike: null,
					})
				}
			} catch (error) {
				if ((error as ErrorResponse)?.code === "BikeVersionMismatchException") {
					// BikeVersionMismatchException === code already send
					// We redirect to the 2nd step even if we don't have info from API
					bikeRegistrationDispatch({
						type: BikeRegistrationActionType.CODE_VALIDATION_STEP,
						bikeInfo: null,
						bikeId: bikeId,
						bike: null,
					})
				} else if ((error as ErrorResponse)?.code === "RessourceNotFoundException") {
					// RessourceNotFoundException === Bike not found
					// We search the bikeId in bikesList
					const existingBike = bikesList.find((bike) => bike.bike_id === bikeId)
					if (existingBike) {
						// If we have a bike :
						// First, we check if action needed
						if (existingBike.bike_action_needed) {
							alert("error", t("bike_need_action", { ns: "form" }))
						}
						// Then, we check if bike is sync with fnuci
						else if (existingBike.is_sync_with_fnuci === IS_FNUCI_SYNC) {
							alert("success", t("bike_already_in_list", { ns: "form" }))
						} else {
							// If not sync with fnuci, we redirect user to register page
							alert("success", t("bike_already_in_list_with_update", { ns: "form" }))
							bikeRegistrationDispatch({
								type: BikeRegistrationActionType.BIKE_REGISTRATION_STEP,
								bikeId: existingBike.bike_id,
								bikeInfo: null,
								bike: existingBike,
							})
						}
					} else {
						const errorMsg = (error as ErrorResponse)?.message?.toString()
						alert("error", errorMsg)
					}
				} else {
					const errorMsg = (error as ErrorResponse)?.message?.toString()
					alert("error", errorMsg)
				}
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	const mainUrl =
		process.env.REACT_APP_MODE === "production"
			? `${window.location.origin}`
			: `${process.env.PUBLIC_URL}`

	return (
		<>
			<div className="flex justify-center items-center mb-7 lg:w-3/4 mx-auto">
				{currentOperator.id === BICYCODE_ID && (
					<img
						src={`${mainUrl}/images/bicycode_tag.png`}
						alt={t("add_modal.bicycode_tag", {
							ns: `bike_${currentOperator.folderName}`,
						})}
						width="65"
						height="169"
					/>
				)}
				{currentOperator.id === DECATHLON_ID && (
					<img
						src={`${mainUrl}/images/decathlon_tag.png`}
						alt={t("add_modal.decathlon_tag", {
							ns: `bike_${currentOperator.folderName}`,
						})}
						width="65"
						height="169"
					/>
				)}
				{currentOperator.id === MFC_ID && (
					<img src={`${mainUrl}/images/mfc_tag.jpg`} alt="MFC" width="65" height="201" />
				)}
				<p className="pl-8 w-3/4">{t("add_modal.description", { ns: "bike" })}</p>
			</div>
			<form>
				<FormElement
					{...formElementCommonProps}
					value={formFields.bike_id}
					name="bike_id"
					type="text"
					label={t("dashboard.register_code", {
						ns: "bike",
					})}
					className="w-full lg:w-3/4 px-1 mb-6 mx-auto"
					required={true}
				/>

				{isReady && (
					<div className="flex justify-center">
						<Button
							onClick={handleCodePassword}
							className="mx-2 px-16 py-1 min-w-min"
							variant="secondary"
							disabled={formFields.bike_id.length < BIKE_ID_LENGTH}
						>
							{t("global.confirm")}
						</Button>
						<Button
							onClick={() => setModalIsOpen(false)}
							className="mx-2 px-6 py-1 min-w-min"
							variant="secondaryBordered"
						>
							{t("global.cancel")}
						</Button>
					</div>
				)}

				{isLoading && (
					<div className="flex justify-center my-8">
						<Spinner color={"var(--primaryColor)"} size="100px" />
					</div>
				)}
			</form>
		</>
	)
}
